import React, { useState } from "react";
import { Form, Field } from "react-final-form";
import {
  ANALYSER_PROPERTY_TYPES,
  COUNTRY,
  COUNTRY_OPTIONS,
  GET_LENGTH_OPTIONS,
  MEMBER_KEY,
  PROPERTY_TYPE,
} from "../../../utils/constants";
import { REIButton, REICheckBox, REIDropDown, REIInputNumber } from "../../../components/ui";
import { ApiEndPoints } from "../../../utils/endpoints";
import { useHttpGet } from "../../../services/httpService";
import { BathIcon, BedIcon, CarIcon } from "../../../public/assets/images";
import AddressAutoComplete from "../../../components/shared/addressAutoComplete";
import { useNavigate } from "react-router-dom";
import { IPropertyAnalyserMedianRequest } from "../../../interface/analysed";

const AddNewProperty: React.FC = () => {
  const { data: propertyTypes, error } = useHttpGet(`${ApiEndPoints.anylyserfilter}/WBPropertyType`, undefined, true);
  const [defauleProfile, setDefauleProfile] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [state, setState] = useState<string | null>(null);
  const [postcode, setPostcode] = useState<string | null>(null);
  const [statsSuburb, setStatsSuburb] = useState<string | null>(null);
  const navigate = useNavigate();

  const getBedRoomOptions = () => [...GET_LENGTH_OPTIONS(0, 9, (i) => (i === 9 ? "9+" : i.toString()), 2)];

  const getBathCarOptions = () => [...GET_LENGTH_OPTIONS(0, 5, (i) => (i === 5 ? "5+" : i.toString()), 2)];

  const onSubmit = async (values: any) => {
    const body = {
      BathroomId: values.bath.toString(),
      BedroomId: values.bed.toString(),
      CarportId: values.car.toString(),
      CountryId: values.country,
      DateAdded: new Date().toLocaleDateString("en-GB"),
      Landsize: values.landArea?.toString(),
      MemberPropertyId: 0,
      ModulesId: localStorage.getItem(MEMBER_KEY),
      PropertyDetailId: 0,
      PropertyName: address,
      PropertyTypeId: values.propertyType,
      UserCountryId: 1,
      district: null,
      locality: statsSuburb ? statsSuburb : address ? address.split(",")[0] : null,
      postcode: postcode,
      state: state,
      useDefault: defauleProfile ? "1" : "0",
      statsSuburb: statsSuburb ? statsSuburb : address ? address.split(",")[0] : null,
    } as IPropertyAnalyserMedianRequest;
    navigate("/analyse-property", {
      state: { propertyAnalyserId: 0, medianRequestBody: body },
    });
  };

  const validate = () => {
    const errors: { selectedPlace?: string } = {};

    if (!address) {
      errors.selectedPlace = "Property name or address is required.";
    }

    return errors;
  };

  const handlePlaceSelected = (place: google.maps.places.PlaceResult | null, address: string | null) => {
    if (
      place &&
      place.types &&
      place.types.length > 0 &&
      (place.types[0].includes("street_address") ||
        place.types[0].includes("premise") ||
        place.types[0].includes("subpremise"))
    ) {
      setShowAddressWarning(false);
    } else {
      setShowAddressWarning(true);
    }
    setAddress(address);
    if (place?.address_components) {
      const stateComponent = place.address_components.find((component) =>
        component.types.includes("administrative_area_level_1")
      );
      if (stateComponent) {
        setState(stateComponent.short_name);
      }
      const postcodeComponent = place.address_components.find((component) => component.types.includes("postal_code"));
      if (postcodeComponent) {
        setPostcode(postcodeComponent.short_name);
      }
      const suburbComponent = place.address_components.find(
        (component) => component.types.includes("sublocality") || component.types.includes("locality")
      );
      if (suburbComponent) {
        setStatsSuburb(suburbComponent.short_name);
      }
    }
  };

  const onDefauleProfileChange = (e: any) => {
    setDefauleProfile(e.checked);
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        country: 1,
        landArea: "",
        bed: 2,
        bath: 2,
        car: 2,
        propertyType: 1,
        defauleProfile: false,
      }}
      validate={validate}
      render={({ handleSubmit, submitting, valid, values }) => (
        <div className="bg-white p-2">
        <form onSubmit={handleSubmit} className="p-fluid">
          <div className="flex md:flex-row flex-col gap-2 mb-2">
            <Field name="country">
              {({ input }) => (
                <div className="md:w-1/4 w-full">
                  <label className="block text-sm font-medium">Country</label>
                  <REIDropDown
                    {...input}
                    id="panp-drop-country"
                    data={COUNTRY_OPTIONS}
                    label="name"
                    optionValue="value"
                    onSelectionChange={(e) => input.onChange(e.value)}
                  />
                </div>
              )}
            </Field>

            <Field name="selectedPlace">
              {({ input, meta }) => (
                <div className="md:w-3/4 w-full">
                  <label className="block text-sm font-medium">Property name or address</label>
                  <AddressAutoComplete
                    onPlaceSelected={handlePlaceSelected}
                    country={values.country == 1 ? COUNTRY.AU : COUNTRY.NZ}
                  />
                  {meta.error && meta.touched && <span className="text-red-500 text-sm">{meta.error}</span>}
                  {showAddressWarning && (
                    <span className="text-sm">
                      Your address is not recognised as a property address, which may result in some statistics and data
                      being unavailable for your analysis.
                    </span>
                  )}
                </div>
              )}
            </Field>
          </div>

          <div className="flex md:flex-row flex-col gap-2 mt-1 mb-2">
            <Field name="propertyType">
              {({ input }) => (
                <div className="md:w-1/4 w-full">
                  <label className="block text-sm font-medium">Property type</label>
                  <REIDropDown
                    {...input}
                    id="panp-drop-propertytype"
                    data={ANALYSER_PROPERTY_TYPES}
                    label="type"
                    optionValue="value"
                    onSelectionChange={(e) => input.onChange(e.value)}
                  />
                </div>
              )}
            </Field>

            <Field name="landArea">
              {({ input }) => (
                <div className="md:w-1/4 w-full">
                  <label className="block text-sm font-medium">Land area (m2)</label>
                  <REIInputNumber
                    {...input}
                    id="panp-num-landarea"
                    className="w-full"
                    maxFractionDigits={0}
                    min={0}
                    max={100000}
                    onValueChange={(e) => input.onChange(e.value)}
                  />
                </div>
              )}
            </Field>

            <div className="md:w-2/4 w-full">
              <div className="flex md:flex-row flex-col gap-2 items-center h-full mt-2 ml-1">
                <Field name="bed">
                  {({ input, meta }) => (
                    <div className="md:w-1/3 w-full flex items-center h-full">
                      <img title="Number of bedrooms" src={BedIcon} alt="Bed" className="h-8 w-auto" />
                      <REIDropDown
                        {...input}
                        id="panp-drop-bed"
                        data={getBedRoomOptions()}
                        label="Label"
                        className="w-full"
                        optionValue="value"
                        onSelectionChange={(e) => {
                          input.onChange(e.value);
                          values.bed = e.value;
                        }}
                      />
                    </div>
                  )}
                </Field>

                <Field name="bath">
                  {({ input, meta }) => (
                    <div className="md:w-1/3 w-full flex items-center h-full">
                      <img src={BathIcon} title="Number of bathrooms" alt="Bath" className="h-8 w-auto" />
                      <REIDropDown
                        {...input}
                        id="panp-drop-bath"
                        data={getBathCarOptions()}
                        label="Label"
                        className="w-full"
                        optionValue="value"
                        onSelectionChange={(e) => {
                          input.onChange(e.value);
                          values.bath = e.value;
                        }}
                      />
                    </div>
                  )}
                </Field>

                <Field name="car">
                  {({ input, meta }) => (
                    <div className="md:w-1/3 w-full flex items-center h-full">
                      <img src={CarIcon} title="Number of parking spaces" alt="Park" className="h-8 w-auto" />
                      <REIDropDown
                        {...input}
                        id="panp-drop-car"
                        data={getBathCarOptions()}
                        label="Label"
                        className="w-full"
                        optionValue="value"
                        onSelectionChange={(e) => {
                          input.onChange(e.value);
                          values.car = e.value;
                        }}
                      />
                    </div>
                  )}
                </Field>
              </div>
            </div>
          </div>

          <div className="flex md:flex-row flex-col gap-2 mt-2">
            <Field name="defauleProfile" type="checkbox">
              {({ input }) => (
                <div className="md:w-3/4 w-full text-start">
                  <REICheckBox
                    {...input}
                    id="panp-chk-defaultprofile"
                    className="mr-1 rounded-xl"
                    label="Use my default profile"
                    checked={defauleProfile}
                    onChange={onDefauleProfileChange}
                  />
                </div>
              )}
            </Field>

            <div className="md:w-1/4 w-full text-end">
              <REIButton
                id="panp-btn-addproperty"
                label="Add property"
                type="submit"
                disabled={submitting || !valid}
                icon=""
                loading={submitting}
                classNames="w-full"
                onClick={() => {}}
              />
            </div>
          </div>
        </form>
        </div>
      )}
    />
  );
};

export default AddNewProperty;
